import React, { useEffect, useState } from 'react';
import { Alert, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SwitchField from '../common/SwitchField';
import {
  disablePushNotifications,
  enablePushNotifications,
  isPushSupported,
  readPushPermission,
  syncPushSubscription,
} from '../../utils/pushNotifications';
import { dismissPushPrompt, notifyPushEnabled, subscribePushEnabled } from '../../utils/pushPrompt';

const PushNotificationControl = ({ audience, compact = false, onEnableRequest }) => {
  const { t } = useTranslation();
  const supported = isPushSupported();
  const [enabled, setEnabled] = useState(false);
  const [status, setStatus] = useState(() => {
    if (!supported) return 'unsupported';
    if (readPushPermission() === 'denied') return 'denied';
    return 'ready';
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported || readPushPermission() !== 'granted') {
      return undefined;
    }

    let cancelled = false;
    syncPushSubscription(audience)
      .then((active) => {
        if (!cancelled && active) {
          setEnabled(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('error');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [audience, supported]);

  useEffect(() => subscribePushEnabled((enabledAudience) => {
    if (enabledAudience === audience) {
      setEnabled(true);
      setStatus('ready');
    }
  }), [audience]);

  const handleChange = async (event) => {
    const nextEnabled = event.target.checked;
    if (nextEnabled && onEnableRequest) {
      onEnableRequest();
      return;
    }

    setBusy(true);
    setStatus('ready');
    try {
      if (nextEnabled) {
        const result = await enablePushNotifications(audience);
        if (result === 'enabled') {
          dismissPushPrompt(audience);
          notifyPushEnabled(audience);
          setEnabled(true);
          setStatus('ready');
          return;
        }
        if (result === 'dismissed') {
          setEnabled(false);
          setStatus('ready');
          return;
        }
        setEnabled(false);
        setStatus(result);
        return;
      }

      await disablePushNotifications(audience);
      setEnabled(false);
    } catch (error) {
      console.error('Failed to update push notifications', error);
      setStatus('error');
    } finally {
      setBusy(false);
    }
  };

  const hintKey = audience === 'customer'
    ? 'pushNotifications.customerHint'
    : 'pushNotifications.staffHint';
  const statusMessage = status === 'ready'
    ? null
    : t(`pushNotifications.${status}`);

  return (
    <Box>
      <SwitchField
        checked={enabled}
        onChange={handleChange}
        disabled={!supported || status === 'denied' || busy}
        label={t('pushNotifications.enable')}
        helpText={compact ? null : t(hintKey)}
      />
      {statusMessage ? (
        <Alert severity={status === 'error' ? 'error' : 'info'} sx={{ mt: 1 }}>
          {statusMessage}
        </Alert>
      ) : null}
    </Box>
  );
};

export default PushNotificationControl;
